// ============================================================================
// "Change what this is." Addendum part 2A. 2026-09-03.
//
// Setting a class on the way IN is a write to a fresh record and shipped in
// #454. Changing what a record IS after Finance has seen it is this, and I held
// it back saying it "has preconditions and a logging obligation of its own".
// Here is what those actually turned out to be, measured rather than assumed.
//
// ⭐ THE FINDING THAT MAKES THIS SAFE: RECLASSIFYING MOVES NO MONEY. Exactly
// three things in the database read `event_class` — `event_class_for_division`
// (a mapping), `v_event_classes` (the catalog) and `v_classification_queue`
// (Jose's queue). No invoice function, no margin trigger, no payment path reads
// it; invoice lines come from `project_services` and margin is computed by a
// trigger from bill and pay. So changing the class of an invoiced record does
// not re-bill anybody, and the reason to be careful is a REPORTING reason, not
// a financial one. I checked this before building rather than after.
//
// ⛔ AUTHORIZATION IS ALREADY DECIDED AND IS NOT RE-OPENED HERE. TCSD-0170: any
// full logged-in employee may edit a project including after money has moved,
// and the audit trail is the control. Ben: *"we keep making the system unusable
// with restrictions."* So there is no role gate on this, and adding one would
// need to supersede that entry.
//
// ⛔ WHAT IS REQUIRED IS A REASON, NOT A PERMISSION. `audit_log` already records
// every UPDATE with its actor (smoke assertion 14) — what it cannot record is
// WHY. Once Finance has seen a record, changing what it is without a written
// reason leaves the next person reading a category that contradicts the
// invoice, with nothing to explain it. So: a reason in words, on the record, in
// a column a person reads.
//
// ⛔ AND MOVING BACK TO PROJECT CLEARS THE REASON CODE. The CHECK permits a
// reason_code on Project work, so nothing would refuse it — which is exactly why
// it has to be done here. A record filed as ordinary project work still
// carrying `SUB_ABSENCE_FILL` is the stale-vocabulary hazard `chooseKind()`
// already guards against on the entry screen; the same rule applies on the way
// back.
// ============================================================================

/** The class that means "ordinary billable project work". */
export const PROJECT_CLASS = 'Project';
/** The class that means "nobody has decided yet". */
export const UNCLASSIFIED_CLASS = 'Unclassified';

export interface ReclassifyInput {
  /** The class being moved TO. */
  event_class: string;
  /** From the helper. Null on Project and Unclassified, required otherwise. */
  reason_code: string | null;
  /** The words. Required once Finance has seen the record. */
  words: string;
  /**
   * Has this record left the AM's hands? Not "has a payment row" — the moment
   * that matters is when somebody else started relying on the category.
   */
  sent: boolean;
  /** What it is now, so a no-op can be refused rather than logged as a change. */
  current_event_class: string | null;
}

/**
 * What is missing, in the AM's words. Empty means it can be saved.
 *
 * ⛔ These mirror the DB CHECKs rather than inventing new rules —
 * `projects_reason_code_required_for_special` and
 * `projects_reason_note_required_off_project` — so the screen says what is
 * wrong before Postgres has to, in a sentence a person can act on. The `sent`
 * rule is the one addition, and it is about the audit story, not the schema.
 */
export function reclassifyBlockers(input: ReclassifyInput): string[] {
  const out: string[] = [];

  if (input.event_class === input.current_event_class && !input.words.trim()) {
    out.push('a different answer — this is already filed that way');
    return out;
  }

  // projects_reason_code_required_for_special
  if (
    input.event_class !== PROJECT_CLASS &&
    input.event_class !== UNCLASSIFIED_CLASS &&
    !input.reason_code
  ) {
    out.push('which one it was');
  }

  // projects_reason_note_required_off_project
  if (input.event_class !== PROJECT_CLASS && !input.words.trim()) {
    out.push('the reason in words');
  }

  // ⛔ THE ONE RULE THAT IS OURS, NOT THE SCHEMA'S. Finance has already read
  // this category. Changing it silently leaves a record whose class contradicts
  // its invoice with nothing on it to explain the difference.
  if (input.sent && !input.words.trim()) {
    out.push('why it is changing — Finance has already seen this one');
  }

  return Array.from(new Set(out));
}

export interface ReclassifyPatch {
  event_class: string;
  reason_code: string | null;
  reason_note: string;
}

/**
 * The write, given an accepted reclassification.
 *
 * ⛔ THE OLD NOTE IS KEPT, NEVER REPLACED. What a record used to say about
 * itself is the only account of why it was filed that way, and this is the
 * moment somebody would most want to read it. The new reason goes ON TOP, dated
 * and labelled, so the record reads as a history rather than as a claim that it
 * was always this.
 */
export function reclassifyPatch(
  input: ReclassifyInput,
  previousNote: string | null,
  today: string,
): ReclassifyPatch {
  const from = input.current_event_class ?? 'unfiled';
  const line = `[${today}] Re-filed from ${from} to ${input.event_class}. ${input.words.trim()}`.trim();
  const prior = (previousNote ?? '').trim();

  return {
    event_class: input.event_class,
    // ⛔ Moving back to ordinary project work drops the reason code. The CHECK
    // would allow it to stay, which is precisely why it is dropped here.
    reason_code:
      input.event_class === PROJECT_CLASS || input.event_class === UNCLASSIFIED_CLASS
        ? null
        : input.reason_code,
    reason_note: prior ? `${line}\n\n${prior}` : line,
  };
}
